import { signal, WritableSignal } from "@angular/core";
import { GetRowIdParams, GridApi, GridOptions, GridReadyEvent } from "ag-grid-enterprise";
import { AccountManualType } from "@bitwarden/web-vault/app/models/types/account.types";
import { AccountIconRendererComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/account-icon-renderer/account-icon-renderer.component";
import { TableCellNameRendererComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/account-name-renderer/table-cell-name-renderer.component";
import { AccountStatusRendererComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/account-status-renderer/account-status-renderer.component";
import { TableParent } from "@bitwarden/web-vault/app/models/types/table.types";
import {
  AccountDetailsTableSubscription,
  TableSource,
  TableFilter,
} from "./account-details-table.subscription";
import { getRowClass } from "@bitwarden/web-vault/app/components/tables/tables.utils.js";
import { AmountRendererComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/amount-renderer/amount-renderer.component";
import { GridSizeChangedEvent } from "ag-grid-community/dist/types/core/events";

type AccountDetailsTableUIData = {
  icon: string;
  status: string;
  more: string;
  type: AccountManualType;
};
type AccountDetailsTableSource = TableSource & AccountDetailsTableUIData;

export type AccountDetailsTableFilter = TableFilter;

export class AccountDetailsTable extends AccountDetailsTableSubscription {
  gridApi!: GridApi;
  options: GridOptions<AccountDetailsTableSource>;
  accountId: WritableSignal<string | null> = signal<string | null>(null);
  private selectedRowIndex!: number;

  constructor(parent: TableParent) {
    super();

    this.options = {
      context: { parent },
      rowSelection: "single",
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
        this.onRowClicked(this.accountId());
      },
      onGridSizeChanged: (params: GridSizeChangedEvent) => {
        params.api.sizeColumnsToFit();
      },
      suppressCellFocus: true,
      suppressContextMenu: true,
      groupDisplayType: "groupRows",
      rowDragManaged: false,
      groupDefaultExpanded: -1,
      columnDefs: [
        {
          field: "icon",
          cellRenderer: AccountIconRendererComponent,
          minWidth: 45,
          maxWidth: 70,
        },
        {
          field: "name",
          cellRenderer: TableCellNameRendererComponent,
          flex: 3,
          minWidth: 120,
          cellStyle: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0",
          },
        },
        {
          field: "balance",
          cellRenderer: AmountRendererComponent,
          cellRendererParams: { triggerByEye: false },
          flex: 1,
          minWidth: 90,
        },
        {
          field: "status",
          cellRenderer: AccountStatusRendererComponent,
          flex: 2,
          minWidth: 90,
        },
      ],
      getRowId: (params: GetRowIdParams) => String(params.data.id),
      onRowClicked: (params) => params.node.data && this.onRowClicked(params.node.data.id),
      getRowHeight: () => 72,
      getRowClass: (params) => getRowClass(params, this.selectedRowIndex),
    };
  }

  onRowClicked(accountId: string | null): void {
    this.accountId.set(accountId);
  }
}
